import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { localeDe, MbscDatepickerModule } from '@mobiscroll/angular';
import { Appearance } from '@softline/application';
import { DateService, IsoDate, Store, toIsoDate } from '@softline/core';
import { I18nModule } from '../../../i18n/i18n.module';
import { L10nModule } from '../../../l10n/l10n.module';
import { SOFTLINE_FEATURE_MODAL } from '../../../modal/legacy/modal.shared';
import * as ModalStore from '../../../modal/legacy/modal.store';
import { UiCorePipesModule } from '../../../pipes/ui-core-pipes.module';
import { DateParser } from '../../../services/date.parser';
import { ComboBoxOptionDirective } from '../../form/combo-box/combo-box-option/combo-box-option.directive';
import { ComboBoxComponent } from '../../form/combo-box/combo-box.component';
import { SelectOptionDirective } from '../../form/select/select-option/select-option.directive';
import { SelectComponent } from '../../form/select/select.component';

@Component({
  selector: 'soft-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    I18nModule,
    L10nModule,
    UiCorePipesModule,
    SelectComponent,
    SelectOptionDirective,
    ComboBoxComponent,
    ComboBoxOptionDirective,
    MbscDatepickerModule,
  ],
})
export class DatePickerComponent {
  public localeDe = localeDe;

  value = input<string>(this.dateService.now());
  valueChange = output<IsoDate>();

  /*readonly theme = this.store.signal(
    SOFTLINE_FEATURE_SETTINGS,
    SettingsStore.getters.values<Appearance>(),
    SOFTLINE_SETTINGS_APPEARANCE
  );*/

  readonly theme = signal<Appearance | null>(null);

  readonly themeVariant = computed(() => {
    const theme = this.theme()?.theme;
    if (!theme) return 'light';
    if (theme === 'system' || theme.includes('auto')) return 'auto';
    if (theme.includes('dark')) {
      return 'dark';
    }

    return 'light';
  });

  readonly shortcuts: { value: string; label: string }[] = [
    {
      label: 'Gestern',
      value: 'h-1',
    },
    {
      label: 'Vor einer Woche',
      value: 'h-7',
    },
    {
      label: 'Vor einem Monat',
      value: 'h-1m',
    },
    {
      label: 'Vor einem Jahr',
      value: 'h-1j',
    },
    {
      label: 'Wochenanfang',
      value: 'wa',
    },
    {
      label: 'Monatsanfang',
      value: 'ma',
    },
    {
      label: 'Jahresanfang',
      value: 'ja',
    },
  ];

  constructor(
    readonly dateService: DateService,
    readonly dateParser: DateParser<IsoDate>,
    readonly store: Store
  ) {}

  onCellClick(date: Date): void {
    this.valueChange.emit(toIsoDate(date));
  }

  selectShortcut(shortcut: { value: string; label: string }): void {
    const parsedDate = this.parseShortcutDate(shortcut.value);

    if (!parsedDate) return;

    this.valueChange.emit(parsedDate);
  }

  async openShortcutsList(): Promise<void> {
    const result = await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.choose<string>(),
      {
        title: 'Shortcuts',
        dismiss: true,
        options: this.shortcuts.map((o) => ({
          value: o.value,
          label: o.label,
        })),
      }
    );

    if (!result || result === 'DISMISSED') return;

    const shortcut = this.shortcuts.find((o) => o.value === result);

    if (!shortcut) return;

    this.selectShortcut(shortcut);
  }

  private parseShortcutDate(value: string): IsoDate | null {
    const result = this.dateParser.parse(value);

    if (!result) return null;

    return result;
  }
}
